import React, { useEffect } from 'react';
import {
  CatalogIndexPage,
  CatalogTable,
  CatalogTableColumnsFunc,
} from '@backstage/plugin-catalog';
import {
  useEntityList,
  DefaultEntityFilters,
  DefaultFilters,
} from '@backstage/plugin-catalog-react';
import { TeamsNameFilter } from './TeamsNameFilter';
import { getTeamDisplayName } from './teamDisplayName';

/**
 * 自定义过滤器类型
 */
type TeamsFilters = DefaultEntityFilters & {
  teamsName?: TeamsNameFilter;
};

/**
 * 创建团队管理页面的自定义列
 * 显示：名称（原 group 名称）、说明（中文）、操作
 */
const createTeamsColumns: CatalogTableColumnsFunc = (entityListContext) => {
  // 如果是 group 类型，创建自定义列
  if (entityListContext.filters.kind?.value === 'group') {
    // 获取默认列以获取 Actions 列
    const defaultColumns = CatalogTable.defaultColumnsFunc(entityListContext);
    const actionsColumn = defaultColumns.find(
      col => col.id === 'actions' || col.title === 'Actions'
    );
    
    // 使用默认的名称列（保持原 group 名称）
    const nameColumn = CatalogTable.columns.createNameColumn({ defaultKind: 'group' });
    
    // 创建说明列，显示：{systemName} System {角色}
    const descriptionColumn = {
      id: 'description',
      title: '说明',
      field: 'entity.metadata.name',
      render: (row: any) => {
        const groupName = row.entity.metadata.name;
        
        // 提取 System 名称和角色
        let systemName = '';
        let role = '';
        
        if (groupName === 'system-manager') {
          systemName = 'system-manager';
          role = '管理员';
        } else if (groupName.endsWith('-owners')) {
          systemName = groupName.replace(/-owners$/, '');
          role = '所有者';
        } else if (groupName.endsWith('-viewers')) {
          systemName = groupName.replace(/-viewers$/, '');
          role = '只读';
        } else {
          // 其他情况保持原样
          return <span>{groupName}</span>;
        }
        
        // 格式：{systemName} System {角色}
        return <span>{systemName} System {role}</span>;
      },
    };
    
    // 返回：名称、说明、操作
    const columns = [nameColumn, descriptionColumn];
    if (actionsColumn) {
      columns.push(actionsColumn);
    }
    
    return columns;
  }
  
  // 其他类型使用默认列
  return CatalogTable.defaultColumnsFunc(entityListContext);
};

/**
 * 团队过滤器组件：在 filters prop 中使用，应用团队名称过滤器
 */
export const TeamsFilterPicker: React.FC = () => {
  const { updateFilters } = useEntityList<TeamsFilters>();
  
  // 应用团队过滤器
  useEffect(() => {
    updateFilters({
      teamsName: new TeamsNameFilter(),
    });
  }, [updateFilters]);
  
  // 这个组件不需要 UI，只需要应用过滤器
  return null;
};

/**
 * 团队管理页面
 */
export const TeamsPage: React.FC = () => {
  return (
    <CatalogIndexPage
      key="teams"
      initiallySelectedFilter="all"
      filters={
        <>
          <DefaultFilters initialKind="group" />
          <TeamsFilterPicker />
        </>
      }
      columns={createTeamsColumns}
    />
  );
};

